import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Paper,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import RadioButtonUncheckedRoundedIcon from "@mui/icons-material/RadioButtonUncheckedRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";

import {
  tenantSettingsService,
  READINESS_LABELS,
} from "../services/tenantSettingsService";

// Per-tenant hiring configuration.
//
// This page is what makes the module tenant-independent: everything here would
// otherwise be a platform-owned constant, and a new tenant would need a code
// change to onboard. Nothing on this screen references the platform vendor —
// candidates see the tenant's identity, never ours.
//
// No store_id is ever sent. The backend resolves the tenant from the session
// token, so this page has no way to address another tenant even by accident.

const GOOGLE_MODES = [
  {
    value: "APPS_SCRIPT",
    label: "Apps Script push (recommended)",
    help: "You install a small script on your own Google Form. Your Sheet and Drive stay completely private and we hold no Google credentials.",
  },
  {
    value: "TENANT_OAUTH",
    label: "Connect Google account",
    help: "You authorise access to the specific Sheet you pick. Revocable from your Google account at any time.",
  },
  {
    value: "SHARED_SA",
    label: "Shared service account (legacy)",
    help: "You share your Sheet with a platform-owned service account. Simplest, but the credential is not yours and you cannot revoke it yourself.",
  },
];

const AI_MODES = [
  { value: "PLATFORM", label: "Platform key (billed to your account)" },
  { value: "BYOK", label: "Bring your own API key" },
];

const SECTIONS = [
  {
    title: "Identity",
    caption:
      "What candidates see. This appears on invite emails, the booking page and the interview screen.",
    fields: [
      {
        name: "display_name",
        label: "Display name",
        help: "Shown to candidates, e.g. “Acme Retail Careers”.",
      },
      {
        name: "legal_entity_name",
        label: "Legal entity name",
        help: "Used in the consent notice. Must be the entity that actually collects the data — a notice naming the wrong entity is not valid consent.",
      },
      { name: "careers_url", label: "Careers page URL" },
      { name: "logo_url", label: "Logo URL" },
      {
        name: "primary_color",
        label: "Primary colour",
        placeholder: "#1A73E8",
      },
    ],
  },
  {
    title: "Locale",
    caption:
      "Timezone drives interview slot generation — get this wrong and candidates are offered slots at the wrong local time.",
    fields: [
      {
        name: "timezone",
        label: "Timezone (IANA)",
        placeholder: "Asia/Kolkata",
        help: "e.g. Asia/Kolkata, Asia/Dubai, Europe/London.",
      },
      { name: "locale", label: "Locale", placeholder: "en-IN" },
      {
        name: "stt_language_hints",
        label: "Speech language hints",
        placeholder: "en-IN,hi-IN,kn-IN",
        help: "Comma-separated. Improves transcription accuracy for code-switched answers.",
      },
    ],
  },
  {
    title: "Candidate email",
    caption:
      "Invites and outcome emails are sent from this address, so they come from you rather than from us.",
    fields: [
      { name: "mail_from_name", label: "Sender name" },
      { name: "mail_from_address", label: "Sender email" },
      { name: "mail_reply_to", label: "Reply-to address" },
    ],
  },
  {
    title: "Consent",
    caption:
      "Required before any candidate data is collected. Recording consent is separate because a candidate may agree to one and not the other.",
    fields: [
      {
        name: "consent_version",
        label: "Consent version",
        placeholder: "v1",
        help: "Pins which wording each candidate accepted, so a later edit is not retroactive.",
      },
      { name: "consent_text", label: "Data-use consent text", multiline: 4 },
      {
        name: "recording_consent_text",
        label: "Video recording consent text",
        multiline: 3,
      },
    ],
  },
  {
    title: "Retention",
    caption:
      "Interview video and resumes are purged automatically once past these windows.",
    fields: [
      {
        name: "video_retention_days",
        label: "Video retention (days)",
        type: "number",
      },
      {
        name: "resume_retention_days",
        label: "Resume retention (days)",
        type: "number",
      },
    ],
  },
];

const TenantSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [provisioned, setProvisioned] = useState(false);
  const [form, setForm] = useState({});
  const [secrets, setSecrets] = useState({});
  const [readiness, setReadiness] = useState(null);
  const [spend, setSpend] = useState(null);
  const [secretDraft, setSecretDraft] = useState({});
  const [snack, setSnack] = useState({ open: false, msg: "", sev: "success" });
  const [error, setError] = useState(null);

  const notify = (msg, sev = "success") =>
    setSnack({ open: true, msg, sev });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: res } = await tenantSettingsService.get();
      const d = res?.data || {};
      setProvisioned(Boolean(d.provisioned));

      if (d.provisioned) {
        setForm(d);
        setSecrets(d.secrets || {});
        setReadiness(d.readiness || null);
        try {
          const { data: s } = await tenantSettingsService.spend();
          setSpend(s?.data || null);
        } catch {
          // Spend is informational; a failure here must not block the page.
        }
      }
    } catch (e) {
      // 401 means the session token is missing or expired — the most likely
      // cause is signing in before the backend started issuing tokens.
      const status = e?.response?.status;
      setError(
        status === 401
          ? "Your session does not carry a tenant token. Sign out and sign in again."
          : e?.response?.data?.statusMessage || "Failed to load settings",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleField = (name) => (e) =>
    setForm((prev) => ({ ...prev, [name]: e.target.value }));

  const handleProvision = async () => {
    setSaving(true);
    try {
      await tenantSettingsService.provision({
        timezone: "Asia/Kolkata",
        locale: "en-IN",
      });
      notify("Hiring module provisioned for your organisation");
      await load();
    } catch (e) {
      notify(
        e?.response?.data?.statusMessage || "Provisioning failed",
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Send only the editable fields; the server ignores anything else, but
      // there is no reason to make it do that work.
      const patch = {};
      for (const section of SECTIONS) {
        for (const f of section.fields) {
          if (form[f.name] !== undefined && form[f.name] !== null) {
            patch[f.name] = form[f.name];
          }
        }
      }
      patch.google_auth_mode = form.google_auth_mode;
      patch.ai_key_mode = form.ai_key_mode;
      if (form.ai_monthly_budget_usd !== undefined) {
        patch.ai_monthly_budget_usd = form.ai_monthly_budget_usd;
      }

      const { data: res } = await tenantSettingsService.update(patch);
      const d = res?.data || {};
      setReadiness(d.readiness || null);
      if (d.ignored_fields?.length) {
        notify(`Saved. Ignored: ${d.ignored_fields.join(", ")}`, "info");
      } else {
        notify("Settings saved");
      }
    } catch (e) {
      notify(e?.response?.data?.statusMessage || "Save failed", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSecret = async (field) => {
    const value = secretDraft[field];
    if (!value) return;
    try {
      const { data: res } = await tenantSettingsService.setSecret(field, value);
      notify(`Stored (${res?.data?.hint || "hidden"})`);
      setSecretDraft((p) => ({ ...p, [field]: "" }));
      await load();
    } catch (e) {
      notify(
        e?.response?.data?.statusMessage || "Could not store secret",
        "error",
      );
    }
  };

  const handleClearSecret = async (field) => {
    try {
      await tenantSettingsService.clearSecret(field);
      notify("Secret cleared");
      await load();
    } catch (e) {
      notify(e?.response?.data?.statusMessage || "Could not clear", "error");
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ maxWidth: 720, mx: "auto", py: 4 }}>
        <Alert severity="error" action={<Button onClick={load}>Retry</Button>}>
          {error}
        </Alert>
      </Box>
    );
  }

  if (!provisioned) {
    return (
      <Box sx={{ maxWidth: 640, mx: "auto", py: 6 }}>
        <Paper sx={{ p: 4, borderRadius: 3 }}>
          <Typography sx={{ fontSize: 20, fontWeight: 700, mb: 1 }}>
            Set up hiring for your organisation
          </Typography>
          <Typography sx={{ color: "text.secondary", mb: 3 }}>
            The hiring module keeps its configuration per organisation — your
            branding, your sender address, your consent wording, your retention
            windows. Nothing is shared with other organisations, and candidates
            only ever see your identity.
          </Typography>
          <Button
            variant="contained"
            size="large"
            disabled={saving}
            onClick={handleProvision}
          >
            {saving ? "Setting up…" : "Set up hiring"}
          </Button>
        </Paper>
      </Box>
    );
  }

  const secretRow = (field, label, help) => {
    const isSet = secrets[`${field}_set`];
    return (
      <Box key={field} sx={{ mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{label}</Typography>
          <Chip
            size="small"
            icon={isSet ? <LockRoundedIcon /> : undefined}
            label={isSet ? "Stored" : "Not set"}
            color={isSet ? "success" : "default"}
            variant={isSet ? "filled" : "outlined"}
          />
        </Box>
        {help && (
          <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 1 }}>
            {help}
          </Typography>
        )}
        <TextField
          fullWidth
          size="small"
          type="password"
          placeholder={isSet ? "Enter a new value to replace" : "Paste value"}
          value={secretDraft[field] || ""}
          onChange={(e) =>
            setSecretDraft((p) => ({ ...p, [field]: e.target.value }))
          }
          disabled={!secrets.encryption_configured}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Button
                  size="small"
                  onClick={() => handleSaveSecret(field)}
                  disabled={!secretDraft[field]}
                >
                  Save
                </Button>
                {isSet && (
                  <Tooltip title="Remove stored value">
                    <IconButton
                      size="small"
                      onClick={() => handleClearSecret(field)}
                    >
                      <DeleteOutlineRoundedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </InputAdornment>
            ),
          }}
        />
      </Box>
    );
  };

  return (
    <Box sx={{ maxWidth: 1100, mx: "auto", py: 3, px: { xs: 1.5, md: 0 } }}>
      <Typography sx={{ fontSize: 22, fontWeight: 700, mb: 0.5 }}>
        Hiring settings
      </Typography>
      <Typography sx={{ color: "text.secondary", mb: 3, fontSize: 14 }}>
        Configuration for your organisation only. Candidates see your identity
        here — never the platform's.
      </Typography>

      {readiness && !readiness.is_ready && (
        <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
          <Typography sx={{ fontWeight: 600, mb: 0.5 }}>
            Complete these before going live
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {readiness.missing.map((k) => (
              <li key={k}>
                <Typography sx={{ fontSize: 13 }}>
                  {READINESS_LABELS[k] || k}
                </Typography>
              </li>
            ))}
          </Box>
        </Alert>
      )}

      {readiness?.is_ready && (
        <Alert
          icon={<CheckCircleRoundedIcon fontSize="inherit" />}
          severity="success"
          sx={{ mb: 3, borderRadius: 2 }}
        >
          Ready to run hiring.
        </Alert>
      )}

      {/* Separate from the checklist above because it is not this tenant's job.
          Shown either way — a verified sender matters just as much once the
          rest is filled in, and folding it into the amber banner made that
          banner permanent. */}
      {readiness?.platform_pending?.length > 0 && (
        <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
          <Typography sx={{ fontSize: 13 }}>
            Sending domain is not verified yet. Candidate email still goes out,
            but it is more likely to land in spam. Kumudu sets this after the
            DNS records are in place.
          </Typography>
        </Alert>
      )}

      {!secrets.encryption_configured && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          The server has no encryption key configured
          (<code>HIRING_SETTINGS_ENC_KEY</code>), so credentials cannot be stored.
          Secrets are intentionally rejected rather than saved in plain text.
        </Alert>
      )}

      {SECTIONS.map((section) => (
        <Paper key={section.title} sx={{ p: 3, mb: 2.5, borderRadius: 3 }}>
          <Typography sx={{ fontSize: 16, fontWeight: 700 }}>
            {section.title}
          </Typography>
          <Typography
            sx={{ fontSize: 13, color: "text.secondary", mb: 2.5, mt: 0.5 }}
          >
            {section.caption}
          </Typography>
          <Grid container spacing={2}>
            {section.fields.map((f) => (
              <Grid item xs={12} md={f.multiline ? 12 : 6} key={f.name}>
                <TextField
                  fullWidth
                  size="small"
                  type={f.type || "text"}
                  label={f.label}
                  placeholder={f.placeholder}
                  helperText={f.help}
                  multiline={Boolean(f.multiline)}
                  minRows={f.multiline || undefined}
                  value={form[f.name] ?? ""}
                  onChange={handleField(f.name)}
                />
              </Grid>
            ))}
          </Grid>
        </Paper>
      ))}

      <Paper sx={{ p: 3, mb: 2.5, borderRadius: 3 }}>
        <Typography sx={{ fontSize: 16, fontWeight: 700 }}>
          Application intake
        </Typography>
        <Typography
          sx={{ fontSize: 13, color: "text.secondary", mb: 2.5, mt: 0.5 }}
        >
          How applications reach us from your Google Form.
        </Typography>
        <TextField
          select
          fullWidth
          size="small"
          label="Google access method"
          value={form.google_auth_mode || "APPS_SCRIPT"}
          onChange={handleField("google_auth_mode")}
          helperText={
            GOOGLE_MODES.find((m) => m.value === form.google_auth_mode)?.help ||
            GOOGLE_MODES[0].help
          }
        >
          {GOOGLE_MODES.map((m) => (
            <MenuItem key={m.value} value={m.value}>
              {m.label}
            </MenuItem>
          ))}
        </TextField>

        {form.google_auth_mode === "TENANT_OAUTH" &&
          secretRow(
            "google_refresh_token",
            "Google refresh token",
            "Stored encrypted and never displayed again. Revoke it any time from your Google account.",
          )}
      </Paper>

      <Paper sx={{ p: 3, mb: 2.5, borderRadius: 3 }}>
        <Typography sx={{ fontSize: 16, fontWeight: 700 }}>
          AI usage
        </Typography>
        <Typography
          sx={{ fontSize: 13, color: "text.secondary", mb: 2.5, mt: 0.5 }}
        >
          Resume ranking, question generation and interview scoring. The cap stops
          spend for your organisation only.
        </Typography>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={6}>
            <TextField
              select
              fullWidth
              size="small"
              label="API key"
              value={form.ai_key_mode || "PLATFORM"}
              onChange={handleField("ai_key_mode")}
            >
              {AI_MODES.map((m) => (
                <MenuItem key={m.value} value={m.value}>
                  {m.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Monthly cap (USD)"
              value={form.ai_monthly_budget_usd ?? ""}
              onChange={handleField("ai_monthly_budget_usd")}
              helperText="Leave blank for no cap."
            />
          </Grid>
        </Grid>

        {form.ai_key_mode === "BYOK" &&
          secretRow(
            "ai_api_key",
            "Your API key",
            "Stored encrypted. Usage is billed to your own provider account.",
          )}

        {spend && (
          <Box sx={{ mt: 1 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                mb: 0.5,
              }}
            >
              <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                This month{spend.period ? ` (${spend.period})` : ""}
              </Typography>
              <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                ${spend.spent_usd}
                {spend.cap_usd ? ` / $${spend.cap_usd}` : ""}
              </Typography>
            </Box>
            {spend.cap_usd != null && (
              <LinearProgress
                variant="determinate"
                value={Math.min(spend.percent_used ?? 0, 100)}
                color={spend.over_cap ? "error" : "primary"}
                sx={{ height: 6, borderRadius: 3 }}
              />
            )}
            {spend.over_cap && (
              <Typography sx={{ fontSize: 12, color: "error.main", mt: 0.5 }}>
                Monthly cap reached — AI steps are paused until the cap is raised.
              </Typography>
            )}
          </Box>
        )}
      </Paper>

      <Divider sx={{ my: 3 }} />

      <Box sx={{ display: "flex", gap: 2, alignItems: "center", pb: 6 }}>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? "Saving…" : "Save settings"}
        </Button>
        <Button onClick={load} disabled={saving}>
          Discard changes
        </Button>
        {form.timezone && (
          <Typography sx={{ fontSize: 12, color: "text.secondary", ml: "auto" }}>
            Slots will be generated in {form.timezone}
          </Typography>
        )}
      </Box>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snack.sev} variant="filled">
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TenantSettings;
